<script setup lang="ts">
import {computed, onBeforeUnmount, ref, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import ChatNewGroupDrawer from "@/components/navBar/chat/ChatNewGroupDrawer.vue";
import ChatNewConversationDrawer from "@/components/navBar/chat/ChatNewConversationDrawer.vue";
import ChatListItem from "@/components/navBar/chat/ChatListItem.vue";
import {ChatDisplayItem, ChatMetadata} from "@/utils/presenters/interfaces.ts";
import {Screen} from "quasar";
import NamaLabel from "@/components/NamaLabel.vue";
import EntityReferenceData = namaTypes.EntityReferenceData;
import ChatMessageSearchResult = namaTypes.ChatMessageSearchResult;

const props = defineProps<{
  presenter: ChatPresenter;
}>();

const emit = defineEmits(['selectChatConversation', 'closeChatInfo']);
const drawer = ref(true);
const newChatDrawer = ref(false);
const newGroupDrawer = ref(false);
const tab = ref('allChats');
const users = ref<EntityReferenceData[] | undefined>(undefined);
const searchValue = ref('');
const messageSearchResults = ref<ChatMessageSearchResult[]>([]);
const isSearchingMessages = ref(false);
let searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

const sortedChatMetadata = computed(() => {
  if (!props.presenter.chatMetadataList.value) return [];
  return props.presenter.chatMetadataList.value.sort((a, b) => {
    const getTime = (item: typeof a) => item.lastMessageAt ? new Date(item.lastMessageAt).getTime() : (item.createdAt ? new Date(item.createdAt).getTime() : 0);
    return getTime(b) - getTime(a);
  });
});

const hasSearchQuery = computed(() => !!searchValue.value?.trim());

const chatMetadataMap = computed(() => {
  const map = new Map<string, ChatMetadata>();
  for (const chat of sortedChatMetadata.value)
    if (chat.id) map.set(chat.id, chat);
  return map;
});

const titleMatchedItems = computed<ChatDisplayItem[]>(() => {
  const query = searchValue.value?.trim()?.toLowerCase();
  if (!query) return sortedChatMetadata.value.map(chat => ({chat}));
  return sortedChatMetadata.value
    .filter(chat => chat.chatTitle?.toLowerCase().includes(query)
      || chat.chatParticipantsTitle?.toLowerCase().includes(query))
    .map(chat => ({chat}));
});

const messageMatchedItems = computed<ChatDisplayItem[]>(() => {
  const query = searchValue.value?.trim()?.toLowerCase();
  if (!query) return [];
  const titleIds = new Set(titleMatchedItems.value.map(item => item.chat.id));
  const items: ChatDisplayItem[] = [];
  for (const result of messageSearchResults.value) {
    if (!result.conversationId || titleIds.has(result.conversationId))
      continue;
    const chat = chatMetadataMap.value.get(result.conversationId);
    if (chat)
      items.push({chat, snippet: result});
  }
  return items;
});

const groupTitleMatchedItems = computed(() => titleMatchedItems.value.filter(item => item.chat.chatConversationType === 'Group'));
const groupMessageMatchedItems = computed(() => messageMatchedItems.value.filter(item => item.chat.chatConversationType === 'Group'));

watch(searchValue, (val) => {
  if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
  const query = val?.trim();
  if (!query) {
    messageSearchResults.value = [];
    isSearchingMessages.value = false;
    return;
  }
  isSearchingMessages.value = true;
  searchDebounceHandle = setTimeout(async () => {
    messageSearchResults.value = await props.presenter.searchMessages(query);
    isSearchingMessages.value = false;
  }, 500);
});

onBeforeUnmount(() => {
  if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
});

async function fetchUsersIfNeeded() {
  if (!users.value) {
    let fetchedUsers = await props.presenter.fetchUsers()
    // user cannot send message to himself / or add himself to new group
    users.value = fetchedUsers.filter(user => user.id != props.presenter.currentUserInfo?.id);
  }
}

async function startNewChat() {
  newChatDrawer.value = true;
  drawer.value = false;
  await fetchUsersIfNeeded();
}

function backFromNewChat() {
  drawer.value = true;
  newChatDrawer.value = false
}

async function createNewGroup() {
  newChatDrawer.value = false;
  newGroupDrawer.value = true;
  await fetchUsersIfNeeded();
}

function backFromNewGroup() {
  newChatDrawer.value = true;
  newGroupDrawer.value = false
}

function toggleSideBar() {
  newChatDrawer.value = false
  newGroupDrawer.value = false
  if (props.presenter.currentChatMetadata.value || !drawer.value)
    drawer.value = !drawer.value;
}

async function fetchChatAndOpen(item: ChatDisplayItem) {
  emit("closeChatInfo");
  if (Screen.lt.sm)
    drawer.value = false;
  await props.presenter.fetchChatAndOpen(item.chat, item.snippet?.sentAt);
}

function clearSearch() {
  searchValue.value = '';
}

defineExpose({toggleSideBar});
</script>

<template>
  <div class="tw-w-fit tw-pl-3 tw-flex tw-flex-col">
    <ChatNewGroupDrawer
      v-model="newGroupDrawer"
      :presenter="presenter"
      :users="users ?? []"
      @back="backFromNewGroup"
    />
    <ChatNewConversationDrawer
      v-model="newChatDrawer"
      :presenter="presenter"
      :users="users ?? []"
      @back="backFromNewChat"
      @create-new-group="createNewGroup"
    />
    <q-drawer
      v-model="drawer"
      :width="280"
      :breakpoint="500"
      bordered
      class="tw-flex tw-flex-col tw-pb-3 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light tw-text-namaFontColor-darkGray dark:tw-text-namaFontColor-dark"
    >
      <div class="tw-flex-none tw-shrink-0">
        <q-list padding>
          <q-item>
            <q-item-section v-text="Translator.translateId('chats')" class="tw-text-xl"/>
            <q-item-section avatar>
              <q-btn no-caps dense
                     @click="startNewChat"
                     :title="Translator.translateId('newChat')"
                     class="tw-px-2 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-full tw-justify-between">
                <NamaLabel text="newChat" class="!tw-text-xs">
                  <NamaIcon default-icon="add" class="!tw-text-base tw-size-4 tw-pr-1"/>
                </NamaLabel>
              </q-btn>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
      <div class="tw-flex-none tw-shrink-0 tw-px-4 ">
        <div class="tw-relative tw-leading-tight tw-w-full tw-h-full">
                <span class="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-justify-center tw-items-center tw-pl-3">
                  <NamaIcon defaultIcon="search"/>
                </span>
          <input type="text" :placeholder="Translator.translateId('search')" v-model="searchValue"
                 class="tw-border-none tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark
                       dark:hover:tw-bg-namaBackground-bright-dark hover:tw-bg-namaBackground-bright-light
                       dark:focus:tw-bg-namaBackground-bright-dark focus:tw-bg-namaBackground-bright-light
                        tw-shadow tw-appearance-none tw-rounded-md tw-w-full tw-py-1 tw-pl-12  tw-pr-7  tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark
                  focus:tw-ring-0 focus:tw-outline-none focus:tw-shadow-outline dark:tw-placeholder-gray-300" autocomplete="off"
          />
          <span v-if="searchValue" class="tw-opacity-50 tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-justify-center tw-items-center tw-pr-1">
            <NamaIcon defaultIcon="close" class="!tw-text-base tw-font-bold tw-cursor-pointer" @click="clearSearch"/>
          </span>
        </div>
      </div>

      <q-tabs
        v-model="tab"
        dense
        no-caps
        class="text-grey"
        active-color="primary"
        indicator-color="primary"
        align="justify"
        narrow-indicator
      >
        <q-tab name="allChats" :label="Translator.translateId('allChats')" class="nama-tap-label"/>
        <q-tab name="groups" :label="Translator.translateId('groups')" class="nama-tap-label"/>
      </q-tabs>
      <q-tab-panels v-model="tab" animated class="tw-bg-transparent tw-h-full tw-w-full">
        <q-tab-panel name="allChats" class="tw-h-full tw-w-full tw-overflow-y-auto">
          <div class="tw-grow tw-w-full tw-min-h-0 tw-pb-3">
            <div v-if="hasSearchQuery && titleMatchedItems.length > 0">
              <div class="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase"
                   v-text="Translator.translateId('chats')"/>
              <ChatListItem v-for="(item, key) in titleMatchedItems" :key="'t-'+key"
                            :chat="item.chat"
                            :search-query="searchValue.trim()"
                            :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                            :current-user-id="presenter.currentUserInfo?.id ?? ''"
                            :presenter="presenter"
                            @click="fetchChatAndOpen(item)"/>
            </div>
            <div v-if="hasSearchQuery && messageMatchedItems.length > 0">
              <div class="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase"
                   v-text="Translator.translateId('Messages')"/>
              <ChatListItem v-for="(item, key) in messageMatchedItems" :key="'m-'+key"
                            :chat="item.chat"
                            :snippet="item.snippet"
                            :search-query="searchValue.trim()"
                            :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                            :current-user-id="presenter.currentUserInfo?.id ?? ''"
                            :presenter="presenter"
                            @click="fetchChatAndOpen(item)"/>
            </div>
            <template v-if="!hasSearchQuery">
              <ChatListItem v-for="(item, key) in titleMatchedItems" :key="key"
                            :chat="item.chat"
                            :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                            :current-user-id="presenter.currentUserInfo?.id ?? ''"
                            :presenter="presenter"
                            @click="fetchChatAndOpen(item)"/>
            </template>
            <div v-if="isSearchingMessages" class="tw-flex tw-justify-center tw-py-4">
              <q-spinner color="primary" size="24px"/>
            </div>
          </div>
        </q-tab-panel>
        <q-tab-panel name="groups" class="tw-h-full tw-w-full tw-overflow-y-auto">
          <div class="tw-grow tw-w-full tw-min-h-0 tw-pb-3">
            <template v-if="groupTitleMatchedItems.length > 0 || groupMessageMatchedItems.length > 0">
              <div v-if="hasSearchQuery && groupTitleMatchedItems.length > 0">
                <div class="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase"
                     v-text="Translator.translateId('chatSearchChats')"/>
                <ChatListItem v-for="(item, key) in groupTitleMatchedItems" :key="'gt-'+key"
                              :chat="item.chat"
                              :search-query="hasSearchQuery ? searchValue.trim() : ''"
                              :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                              :current-user-id="presenter.currentUserInfo?.id ?? ''"
                              :presenter="presenter"
                              @click="fetchChatAndOpen(item)"/>
              </div>
              <div v-if="hasSearchQuery && groupMessageMatchedItems.length > 0">
                <div class="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-gray-500 dark:tw-text-gray-400 tw-uppercase"
                     v-text="Translator.translateId('chatSearchMessages')"/>
                <ChatListItem v-for="(item, key) in groupMessageMatchedItems" :key="'gm-'+key"
                              :chat="item.chat"
                              :snippet="item.snippet"
                              :search-query="searchValue.trim()"
                              :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                              :current-user-id="presenter.currentUserInfo?.id ?? ''"
                              :presenter="presenter"
                              @click="fetchChatAndOpen(item)"/>
              </div>
              <template v-if="!hasSearchQuery">
                <ChatListItem v-for="(item, key) in groupTitleMatchedItems" :key="key"
                              :chat="item.chat"
                              :is-active="presenter.currentChatMetadata.value?.id === item.chat.id"
                              :current-user-id="presenter.currentUserInfo?.id ?? ''"
                              :presenter="presenter"
                              @click="fetchChatAndOpen(item)"/>
              </template>
              <div v-if="isSearchingMessages" class="tw-flex tw-justify-center tw-py-4">
                <q-spinner color="primary" size="24px"/>
              </div>
            </template>
            <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-gap-3">
              <NamaIcon defaultIcon="group" class="!tw-text-4xl tw-text-gray-400"/>
              <span class="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400"
                    v-text="Translator.translateId('chatNoGroups')"/>
              <q-btn no-caps dense color="primary" @click="createNewGroup"
                     :label="Translator.translateId('createNewGroup')" class="tw-rounded-md tw-px-3"/>
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-drawer>
  </div>
</template>

<style scoped>

</style>
